/**
 * ImageUpload Component
 * Drag-drop and click-to-upload image component
 */

import React, { useCallback, useState, useRef } from 'react';
import { Upload, X } from 'lucide-react';
import { playgroundApi } from '@/lib/api/playground';

export interface UploadedImage {
  id: string;
  file?: File;
  base64: string;
  preview: string;
  filename: string;
}

interface ImageUploadProps {
  onUpload: (images: UploadedImage[]) => void;
  maxImages?: number;
  accept?: string;
  multiple?: boolean;
  existingImages?: UploadedImage[];
  onRemove?: (imageId: string) => void;
  className?: string;
  compact?: boolean;
}

export function ImageUpload({
  onUpload,
  maxImages = 1,
  accept = 'image/*',
  multiple = false,
  existingImages = [],
  onRemove,
  className = '',
  compact = false,
}: ImageUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const processFiles = useCallback(
    async (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      const remainingSlots = maxImages - existingImages.length;

      if (fileArray.length > remainingSlots) {
        setError(`Can only upload ${remainingSlots} more image(s)`);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const uploadedImages: UploadedImage[] = [];

        for (const file of fileArray) {
          // Validate file type
          if (!file.type.startsWith('image/')) {
            setError(`${file.name} is not an image`);
            continue;
          }

          // Validate file size (max 10MB)
          if (file.size > 10 * 1024 * 1024) {
            setError(`${file.name} is too large (max 10MB)`);
            continue;
          }

          // Convert to base64
          const base64 = await playgroundApi.utils.fileToBase64(file);

          // Create preview URL
          const preview = URL.createObjectURL(file);

          uploadedImages.push({
            id: `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            file,
            base64,
            preview,
            filename: file.name,
          });
        }

        if (uploadedImages.length > 0) {
          onUpload(uploadedImages);
        }
      } catch (err) {
        setError('Failed to process image(s)');
        console.error('Upload error:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [maxImages, existingImages.length, onUpload]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        processFiles(files);
      }
    },
    [processFiles]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        processFiles(files);
      }
      // Reset input
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [processFiles]
  );

  const handleClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleRemove = useCallback(
    (imageId: string) => {
      const image = existingImages.find((img) => img.id === imageId);
      if (image?.preview) {
        URL.revokeObjectURL(image.preview);
      }
      onRemove?.(imageId);
    },
    [existingImages, onRemove]
  );

  const canUploadMore = existingImages.length < maxImages;

  if (compact) {
    return (
      <div className={`space-y-2 ${className}`}>
        {/* Existing images */}
        {existingImages.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {existingImages.map((image) => (
              <div key={image.id} className="relative group">
                <img
                  src={image.preview}
                  alt={image.filename}
                  className="w-16 h-16 object-cover rounded border border-gray-200"
                />
                {onRemove && (
                  <button
                    type="button"
                    onClick={() => handleRemove(image.id)}
                    className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Upload button */}
        {canUploadMore && (
          <button
            type="button"
            onClick={handleClick}
            disabled={isLoading}
            className="flex items-center gap-1 px-2 py-1 text-sm text-gray-600 hover:text-primary-600 border border-dashed border-gray-300 hover:border-primary-400 rounded transition-colors"
          >
            <Upload className="w-4 h-4" />
            {isLoading ? 'Uploading...' : 'Upload Image'}
          </button>
        )}

        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple && maxImages > 1}
          onChange={handleInputChange}
          className="hidden"
        />

        {error && <p className="text-xs text-red-500">{error}</p>}
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Drop zone */}
      {canUploadMore && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
          className={`relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            isDragging
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
          } ${isLoading ? 'opacity-50 cursor-wait' : ''}`}
        >
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            multiple={multiple && maxImages > 1}
            onChange={handleInputChange}
            className="hidden"
          />

          <div className="space-y-2">
            <div
              className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center ${
                isDragging ? 'bg-primary-100' : 'bg-gray-100'
              }`}
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Upload
                  className={`w-6 h-6 ${
                    isDragging ? 'text-primary-600' : 'text-gray-400'
                  }`}
                />
              )}
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700">
                {isDragging ? 'Drop image here' : 'Drag & drop image here'}
              </p>
              <p className="text-xs text-gray-500">
                or click to browse (max 10MB)
              </p>
            </div>

            {maxImages > 1 && (
              <p className="text-xs text-gray-400">
                {existingImages.length} / {maxImages} images
              </p>
            )}
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
          {error}
        </div>
      )}

      {/* Uploaded images preview */}
      {existingImages.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {existingImages.map((image) => (
            <div
              key={image.id}
              className="relative group rounded-lg overflow-hidden border border-gray-200"
            >
              <img
                src={image.preview}
                alt={image.filename}
                className="w-full h-32 object-cover"
              />

              {/* Overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors" />

              {/* Remove button */}
              {onRemove && (
                <button
                  type="button"
                  onClick={() => handleRemove(image.id)}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}

              {/* Filename */}
              <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent">
                <p className="text-xs text-white truncate">{image.filename}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
